import { CONSENT_STORAGE_KEY, type ConsentCategory } from '@/lib/analytics/config'

export interface Consent {
  analytics: boolean
  marketing: boolean
  /** ISO timestamp of the decision, so a policy change can re-ask. */
  decidedAt: string
}

/**
 * Consent state, stored per browser.
 *
 * Every read and write is wrapped: private windows, cleared site data and
 * browsers set to block storage all make these throw rather than return null.
 */
export function readConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<Consent>
    if (typeof parsed.analytics !== 'boolean' || typeof parsed.marketing !== 'boolean') return null
    return {
      analytics: parsed.analytics,
      marketing: parsed.marketing,
      decidedAt: parsed.decidedAt ?? new Date().toISOString(),
    }
  } catch {
    return null
  }
}

export function writeConsent(consent: Omit<Consent, 'decidedAt'>): Consent {
  const full: Consent = { ...consent, decidedAt: new Date().toISOString() }
  try {
    localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(full))
  } catch {
    /* storage unavailable — the choice holds for this page view only */
  }
  return full
}

/*
 * Visitors in the EU, EEA, UK and Switzerland must opt in before any tag runs.
 * The site cannot see the visitor's country, so the browser's time zone stands
 * in for it: every Europe/* zone plus the Atlantic and other outlying zones
 * those countries use. Unknown zones count as Europe, the safe side.
 */
const OPT_IN_ZONES = /^(Europe\/|Atlantic\/(Canary|Madeira|Azores|Reykjavik|Faroe)|Africa\/Ceuta|Asia\/Nicosia|Arctic\/Longyearbyen)/

export function needsOptIn(): boolean {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? ''
    return !zone || OPT_IN_ZONES.test(zone)
  } catch {
    return true
  }
}

/** The browser's Global Privacy Control signal: "do not sell or share my data". */
export function sendsGpc(): boolean {
  try {
    return (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  } catch {
    return false
  }
}

/**
 * The consent that applies before a visitor has chosen. Outside Europe, tags
 * run by default and the banner is a notice with a way to decline (as the
 * privacy policy describes); a browser sending GPC gets analytics but no
 * advertising tags. In Europe there is no default: nothing runs until Accept.
 */
export function defaultConsent(): Consent | null {
  if (needsOptIn()) return null
  return { analytics: true, marketing: !sendsGpc(), decidedAt: 'default' }
}

export function allows(consent: Consent | null, category: ConsentCategory): boolean {
  if (category === 'essential') return true
  if (!consent) return false
  return category === 'analytics' ? consent.analytics : consent.marketing
}
